import { cn } from "@/lib/utils";

import { ChoiceBadge } from "../misc/ChoiceBadge";
import type { Task as TData } from "../types";
import { TaskActionsMenu } from "./TaskActions";
import { TaskCheck } from "./TaskCheck";
import { useTaskActions } from "./useTaskActions";
import { TaskDueDate, TaskTarget, TaskTypeLabel } from "./TaskFields";
import { useTaskStatusChoices } from "./taskStatuses";

/** Compact task row of the side panels, the dashboard and the mobile list. */
export const Task = ({
  task,
  showContact,
}: {
  task: TData;
  showContact?: boolean;
}) => {
  const taskStatusChoices = useTaskStatusChoices();
  const actions = useTaskActions(task);

  return (
    <div className="flex items-start gap-2">
      <TaskCheck
        isDone={actions.isDone}
        onToggle={actions.toggleDone}
        className="mt-0.5"
      />
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            "text-sm font-medium",
            actions.isClosed && "text-muted-foreground",
            actions.isDone && "line-through",
          )}
        >
          {task.text}
          {actions.isAbandoned && (
            <span className="ml-2 inline-block text-xs font-normal">
              <ChoiceBadge choices={taskStatusChoices} value="abandoned" />
            </span>
          )}
        </div>
        {task.note && (
          <div className="text-xs text-muted-foreground whitespace-pre-line line-clamp-2">
            {task.note}
          </div>
        )}
        <div className="mt-0.5 flex flex-wrap gap-x-1.5 text-xs text-muted-foreground">
          <TaskDueDate task={task} />
          {task.type && task.type !== "none" && (
            <span>
              · <TaskTypeLabel task={task} />
            </span>
          )}
        </div>
        {showContact && (
          <div className="mt-0.5 text-xs">
            <TaskTarget task={task} withAvatar={false} />
          </div>
        )}
      </div>
      <TaskActionsMenu task={task} actions={actions} />
    </div>
  );
};
